import React from 'react';
import type { CartItemResponse, SelectedAddOn } from '@/features/cart/types';
import { calculateEditModePrice } from '@/features/cart/utils/cartItemUtils';
import { formatCurrencyForDisplay } from '@/lib/utils';



interface CartItemPriceSummaryProps {
  cartItem: CartItemResponse;
  isEditing: boolean;
  editGuestCount: number;
  editAddOnsPerGuest: SelectedAddOn[][];
  removedGuests: Set<number>;
  removedItems: Set<string>;
  clubHandle?: string | null;
  /** Whether this is a table booking (affects price calculation) */
  isTableBooking?: boolean;
  /** Table size/capacity for table bookings */
  tableSize?: number | null;
}

const CartItemPriceSummary: React.FC<CartItemPriceSummaryProps> = ({
  cartItem,
  isEditing,
  editGuestCount,
  editAddOnsPerGuest,
  removedGuests,
  removedItems,
  isTableBooking = false,
  tableSize,
}) => {
  const formatPrice = (amount: number) => formatCurrencyForDisplay(amount, cartItem.currency);

  return (
    <div className="mb-4 p-4 bg-gray-900/50 rounded-lg border border-gray-700/50">
      {isEditing ? (
        <>
          {/* Dynamic price calculation in edit mode */}
          {(() => {
            const priceBreakdown = calculateEditModePrice(
              cartItem,
              editGuestCount,
              editAddOnsPerGuest,
              removedGuests,
              removedItems,
              isTableBooking,
              tableSize
            );

            return (
              <>
                <div className="flex justify-between text-gray-300 mb-2 text-sm">
                  <span>Base Price ({isTableBooking && tableSize ? `Table of ${tableSize}` : `${priceBreakdown.activeGuestCount} × ${formatPrice(priceBreakdown.pricePerGuest)}`})</span>
                  <span>{formatPrice(priceBreakdown.basePrice)}</span>
                </div>
                {priceBreakdown.addOnsPrice > 0 && (
                  <div className="flex justify-between text-gray-300 mb-2 text-sm">
                    <span>Add-ons</span>
                    <span>{formatPrice(priceBreakdown.addOnsPrice)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-300 mb-2 text-sm">
                  <span>Platform Fee</span>
                  <span>{formatPrice(priceBreakdown.platformFee)}</span>
                </div>
                <div className="flex justify-between text-white font-bold text-lg pt-2 border-t border-gray-700">
                  <span>Total</span>
                  <span className="text-blue-400">{formatPrice(priceBreakdown.total)}</span>
                </div>
              </>
            );
          })()}
        </>
      ) : (
        <>
          {(() => {
            // For table bookings, use table_size for base price calculation
            const basePriceMultiplier = isTableBooking && tableSize ? tableSize : cartItem.total_guests;
            const calculatedBasePrice = cartItem.base_price * basePriceMultiplier;
            const basePriceLabel = isTableBooking && tableSize 
              ? `Table of ${tableSize}` 
              : `${cartItem.total_guests} × ${formatPrice(cartItem.base_price)}`;
            
            return (
              <>
                <div className="flex justify-between text-gray-300 mb-2 text-sm">
                  <span>Base Price ({basePriceLabel})</span>
                  <span>{formatPrice(calculatedBasePrice)}</span>
                </div>
                {cartItem.subtotal > calculatedBasePrice && (
                  <div className="flex justify-between text-gray-300 mb-2 text-sm">
                    <span>Add-ons</span>
                    <span>{formatPrice(cartItem.subtotal - calculatedBasePrice)}</span>
                  </div>
                )}
              </>
            );
          })()}
          <div className="flex justify-between text-gray-300 mb-2 text-sm">
            <span>Platform Fee</span>
            <span>{formatPrice(cartItem.platform_fee)}</span>
          </div>
          <div className="flex justify-between text-white font-bold text-lg pt-2 border-t border-gray-700">
            <span>Total</span>
            <span>{formatPrice(cartItem.subtotal + cartItem.platform_fee)}</span>
          </div>
        </>
      )}
    </div>
  );
};

export default CartItemPriceSummary;

